/**
 * Session Details Modal Component
 * Displays detailed information about a charging session
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StatusBadge } from '../common/StatusBadge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  RefreshCw, Zap, MapPin, Clock, User, Battery, 
  CreditCard, Printer, TrendingUp, StopCircle, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatEnergy, formatDuration } from '../../lib/utils';
import api from '../../lib/api';
import { buildPdfFromElement } from '../../lib/pdf';
import ChargingInvoiceDocument, { generateInvoiceNumber, normalizeChargingInvoice } from './ChargingInvoiceDocument';
import { SessionErrorStatusBadge, getSessionFailureText, isSessionGabimStatus } from './SessionFailureNote';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Area, AreaChart, ReferenceLine 
} from 'recharts';

const COMPANY_INFO = {
  name: 'AlbMove Sh.p.k.',
  address: 'Tirane Vore Muçaj Rruga dytesore, Tirane -Vore, 1032, Vore, Tirane',
  city: 'Tiranë, Shqipëri',
  nipt: 'L91234567A',
  phone: '+355 68 200 1818',
  email: 'info@albmove.al',
};

// European date format for Albania timezone
const formatEuropeanDate = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch {
    return '—';
  }
};

const SessionDetailsModal = ({ session, onClose, onRemoteStop }) => {
  const [details, setDetails] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [stoppingSession, setStoppingSession] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const invoiceRef = useRef(null);

  const buildFallbackDetails = useCallback((baseSession, extra = {}) => {
    const mergedSession = {
      ...baseSession,
      ...(extra.session || {}),
    };

    const startSoc = mergedSession.start_soc ?? mergedSession.start_soc_percent ?? mergedSession.soc_start ?? null;
    const endSoc = mergedSession.end_soc ?? mergedSession.end_soc_percent ?? mergedSession.soc_end ?? mergedSession.soc_percent ?? null;

    return {
      session: mergedSession,
      user: extra.user || {
        id: mergedSession.user_id,
        full_name: mergedSession.user_name || mergedSession.client_name || '—',
        email: mergedSession.user_email || mergedSession.client_email || '—',
        phone: mergedSession.user_phone || mergedSession.phone || null,
        user_type: mergedSession.user_type || (String(mergedSession.start_mode || '').toUpperCase() === 'RFID' ? 'RFID' : 'mobile'),
      },
      charger: extra.charger || {
        id: mergedSession.charger_id,
        charger_id: mergedSession.charger_name || mergedSession.charger_id || '—',
        name: mergedSession.charger_name || mergedSession.charger_id || '—',
        vendor: mergedSession.charger_vendor,
        model: mergedSession.charger_model,
      },
      site: extra.site || (mergedSession.site_id || mergedSession.site_name ? {
        id: mergedSession.site_id,
        name: mergedSession.site_name || '—',
        address: mergedSession.site_address || '—',
        city: mergedSession.site_city || '',
      } : null),
      battery: extra.battery || {
        available: startSoc != null || endSoc != null,
        start_soc_percent: startSoc,
        end_soc_percent: endSoc,
        delta_soc_percent: startSoc != null && endSoc != null ? endSoc - startSoc : null,
      },
      connector: extra.connector || (mergedSession.connector_id ? {
        connector_id: mergedSession.connector_id,
        connector_letter: String.fromCharCode(64 + Number(mergedSession.connector_id)),
      } : null),
    };
  }, []);

  const invoiceData = useMemo(() => {
    if (!session) return null;
    const currentSession = details?.session || session;
    const currentUser = details?.user;
    return normalizeChargingInvoice({
      ...currentSession,
      client_name: currentUser?.full_name || currentSession.client_name || currentSession.user_name,
      client_email: currentUser?.email || currentSession.client_email || currentSession.user_email,
      payment_method: currentSession.payment_method || 'Wallet',
      charger_id: details?.charger?.charger_id || currentSession.charger_name || currentSession.charger_id,
      charger_name: details?.charger?.name || currentSession.charger_name || currentSession.charger_id,
      site_name: details?.site?.name || currentSession.site_name,
      charger_price_per_kwh: currentSession.charger_price_per_kwh || details?.charger?.price_per_kwh,
      tariff_lek_per_kwh: currentSession.tariff_lek_per_kwh || currentSession.price_per_kwh || currentSession.charger_price_per_kwh || details?.charger?.price_per_kwh || 40,
    });
  }, [details, session]);

  const loadDetails = useCallback(async () => {
    if (!session?.id) return;

    setLoading(true);
    setDetails(buildFallbackDetails(session));

    const requests = [
      api.get(`/sessions/${session.id}/details`).then((response) => ({ type: 'details', data: response.data })).catch(() => null),
    ];

    if (session.user_id) {
      requests.push(
        api.get(`/admin/clients/${session.user_id}`).then((response) => ({ type: 'user', data: response.data })).catch(async () => {
          const response = await api.get(`/users/${session.user_id}`);
          return { type: 'user', data: response.data };
        })
      );
    }

    if (session.charger_id) {
      requests.push(
        api.get(`/chargers/${session.charger_id}`).then((response) => ({ type: 'charger', data: response.data }))
      );
    }

    if (session.site_id) {
      requests.push(
        api.get(`/sites/${session.site_id}`).then((response) => ({ type: 'site', data: response.data }))
      );
    }

    const results = await Promise.allSettled(requests);
    const extra = {};

    results.forEach((result) => {
      if (result.status !== 'fulfilled' || !result.value) return;
      if (result.value.type === 'details') {
        const payload = result.value.data || {};
        extra.session = {
          ...(payload.session || {}),
          fail_reason: payload.session?.fail_reason ?? session.fail_reason,
          fail_reason_admin: payload.session?.fail_reason_admin ?? session.fail_reason_admin,
          fail_category: payload.session?.fail_category ?? session.fail_category,
        };
        if (payload.user) extra.user = payload.user;
        if (payload.charger) extra.charger = payload.charger;
        if (payload.site) extra.site = payload.site;
        if (payload.battery) extra.battery = payload.battery;
        if (payload.connector) extra.connector = payload.connector;
      }
      if (result.value.type === 'user') {
        extra.user = result.value.data;
      }
      if (result.value.type === 'charger') {
        extra.charger = result.value.data;
      }
      if (result.value.type === 'site') {
        extra.site = result.value.data;
      }
    });

    setDetails(buildFallbackDetails(session, extra));
    setLoading(false);
  }, [buildFallbackDetails, session]);

  const loadMetrics = useCallback(async () => {
    setMetrics([]);
  }, []);

  useEffect(() => {
    if (session?.id) {
      setActiveTab('info');
      loadDetails();
      loadMetrics();
    }
  }, [loadDetails, loadMetrics, session?.id]);

  const generateInvoicePdfBlob = useCallback(async () => {
    if (!invoiceRef.current || !invoiceData) return null;

    const pdf = await buildPdfFromElement(invoiceRef.current);
    return pdf.output('blob');
  }, [invoiceData]);

  const handleDownloadInvoice = async () => {
    try {
      setDownloadingPdf(true);
      const blob = await generateInvoicePdfBlob();
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AlbMove_Fature_${generateInvoiceNumber(invoiceData)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Fatura u shkarkua me sukses');
    } catch (error) {
      console.error('Failed to download invoice:', error);
      toast.error('Gabim në shkarkimin e faturës');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrintInvoice = async () => {
    const printWindow = window.open('', '_blank');
    try {
      if (!printWindow) {
        toast.error('Dritarja e printimit u bllokua nga shfletuesi');
        return;
      }

      const blob = await generateInvoicePdfBlob();
      if (!blob) {
        printWindow.close();
        return;
      }

      const url = window.URL.createObjectURL(blob);
      printWindow.location.href = url;
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } catch (error) {
      console.error('Failed to print invoice:', error);
      if (printWindow) printWindow.close();
      toast.error('Gabim në printimin e faturës');
    }
  };

  // Generate simulated charging data if no real metrics
  const generateChartData = () => {
    if (metrics.length > 0) return metrics;
    
    const s = details?.session || session;
    const duration = s?.duration_minutes || 30;
    const totalEnergy = s?.energy_kwh || 10;
    const startSoc = details?.battery?.start_soc_percent || 20;
    const endSoc = details?.battery?.end_soc_percent || 80;
    
    const points = Math.min(Math.max(10, Math.floor(duration / 3)), 30);
    const data = [];
    
    for (let i = 0; i <= points; i++) {
      const progress = i / points;
      const timeMin = Math.round(progress * duration);
      const chargingCurve = 1 - Math.pow(1 - progress, 0.7);
      const soc = startSoc + (endSoc - startSoc) * chargingCurve;
      const energy = totalEnergy * chargingCurve;
      const maxPower = 22;
      const powerMultiplier = soc < 80 ? 1 : (1 - (soc - 80) / 40);
      const power = maxPower * powerMultiplier * (0.85 + Math.random() * 0.15);
      
      data.push({
        time: timeMin,
        timeLabel: `${timeMin} min`,
        power_kw: Math.round(power * 10) / 10,
        energy_kwh: Math.round(energy * 100) / 100,
        soc: Math.round(soc),
        current_a: Math.round(power * 1000 / 400),
      });
    }
    
    return data;
  };

  if (!session) return null;

  const s = invoiceData || normalizeChargingInvoice({
    ...(details?.session || session),
    charger_price_per_kwh: details?.session?.charger_price_per_kwh || details?.charger?.price_per_kwh,
  }) || details?.session || session;
  const user = details?.user;
  const charger = details?.charger;
  const site = details?.site;
  const battery = details?.battery;
  const connector = details?.connector;
  const chartData = generateChartData();

  return (
    <Dialog open={!!session} onOpenChange={() => onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <DialogHeader className="border-b border-white/10 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Zap className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <DialogTitle className="text-xl text-white font-outfit">
                  {charger?.charger_id || charger?.name || 'Karikues'}
                  {connector && (
                    <span className="ml-2 text-sm text-zinc-400">
                      - Porta {connector.connector_letter}
                    </span>
                  )}
                </DialogTitle>
                <p className="text-xs text-zinc-500 mt-1">
                  ID: <code className="bg-zinc-800 px-1.5 py-0.5 rounded">{s.id}</code>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                s.start_mode === 'Admin' ? 'bg-red-500/20 text-red-400' :
                s.start_mode === 'Remote' ? 'bg-blue-500/20 text-blue-400' :
                s.start_mode === 'QR' ? 'bg-purple-500/20 text-purple-400' :
                'bg-orange-500/20 text-orange-400'
              }`}>
                {s.start_mode === 'Admin' ? '👤 Admin' : s.start_mode || 'Remote'}
              </span>
              {isSessionGabimStatus(s.status) && getSessionFailureText(s) ? (
                <SessionErrorStatusBadge
                  session={s}
                  label="Gabim"
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400"
                />
              ) : (
                <StatusBadge status={s.status} />
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2 mb-4">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              activeTab === 'info' 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'
            }`}
          >
            Informacion
          </button>
          <button
            onClick={() => setActiveTab('chart')}
            className={`px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
              activeTab === 'chart' 
                ? 'bg-purple-500/20 text-purple-400' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Grafiku i Karikimit
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-zinc-500" />
          </div>
        ) : activeTab === 'chart' ? (
          /* Charging Chart Tab */
          <div className="space-y-6 py-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-400">
                  {battery?.start_soc_percent ?? chartData[0]?.soc ?? '—'}%
                </p>
                <p className="text-xs text-zinc-500">SoC Fillim</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-400">
                  {battery?.end_soc_percent ?? chartData[chartData.length - 1]?.soc ?? '—'}%
                </p>
                <p className="text-xs text-zinc-500">SoC Mbarim</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-yellow-400">{formatEnergy(s.energy_kwh)}</p>
                <p className="text-xs text-zinc-500">Konsumi Total</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-purple-400">
                  {Math.max(...chartData.map(d => d.power_kw || 0)).toFixed(1)} kW
                </p>
                <p className="text-xs text-zinc-500">Fuqi Max</p>
              </div>
            </div>

            {/* Main Chart - Power & SoC */}
            <Card className="bg-zinc-800/50 border-white/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-white flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    Fuqia dhe Gjendja e Baterisë
                  </h3>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-yellow-500"></span>
                      Fuqi (kW)
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-emerald-500"></span>
                      SoC (%)
                    </span>
                  </div>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="powerGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#eab308" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="socGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="time" 
                        stroke="#666" 
                        tick={{ fill: '#888', fontSize: 11 }}
                        tickFormatter={(v) => `${v}m`}
                      />
                      <YAxis 
                        yAxisId="power"
                        stroke="#eab308" 
                        tick={{ fill: '#888', fontSize: 11 }}
                        domain={[0, 'auto']}
                        tickFormatter={(v) => `${v}kW`}
                      />
                      <YAxis 
                        yAxisId="soc"
                        orientation="right"
                        stroke="#10b981" 
                        tick={{ fill: '#888', fontSize: 11 }}
                        domain={[0, 100]}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1f1f1f', 
                          border: '1px solid #333',
                          borderRadius: '8px'
                        }}
                        labelFormatter={(v) => `Koha: ${v} min`}
                        formatter={(value, name) => {
                          if (name === 'power_kw') return [`${value} kW`, 'Fuqi'];
                          if (name === 'soc') return [`${value}%`, 'SoC'];
                          return [value, name];
                        }}
                      />
                      <Area 
                        yAxisId="power"
                        type="monotone" 
                        dataKey="power_kw" 
                        stroke="#eab308" 
                        strokeWidth={2}
                        fill="url(#powerGradient)" 
                      />
                      <Area 
                        yAxisId="soc"
                        type="monotone" 
                        dataKey="soc" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        fill="url(#socGradient)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Energy Accumulation Chart */}
            <Card className="bg-zinc-800/50 border-white/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-white flex items-center gap-2">
                    <Battery className="w-4 h-4 text-blue-400" />
                    Energjia e Marrë (kWh)
                  </h3>
                </div>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="energyGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="time" 
                        stroke="#666" 
                        tick={{ fill: '#888', fontSize: 11 }}
                        tickFormatter={(v) => `${v}m`}
                      />
                      <YAxis 
                        stroke="#3b82f6" 
                        tick={{ fill: '#888', fontSize: 11 }}
                        tickFormatter={(v) => `${v}kWh`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1f1f1f', 
                          border: '1px solid #333',
                          borderRadius: '8px'
                        }}
                        labelFormatter={(v) => `Koha: ${v} min`}
                        formatter={(value) => [`${value} kWh`, 'Energji']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="energy_kwh" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        fill="url(#energyGradient)" 
                      />
                      <ReferenceLine 
                        y={s.energy_kwh} 
                        stroke="#3b82f6" 
                        strokeDasharray="5 5"
                        label={{ value: `Total: ${s.energy_kwh} kWh`, fill: '#3b82f6', fontSize: 11 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Info Tab */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* User Info */}
            <Card className="bg-zinc-800/50 border-white/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-sm font-medium text-white">Përdoruesi</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Emri</span>
                    <span className="text-white">{user?.full_name || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Email</span>
                    <span className="text-zinc-300">{user?.email || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Telefon</span>
                    <span className="text-zinc-300">{user?.phone || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Lloji</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      user?.user_type === 'RFID' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {user?.user_type || 'mobile'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Session Metadata */}
            <Card className="bg-zinc-800/50 border-white/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-medium text-white">Të Dhënat e Seancës</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Filloi</span>
                    <span className="text-white">{formatEuropeanDate(s.start_time)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Mbaroi</span>
                    <span className="text-white">{s.end_time ? formatEuropeanDate(s.end_time) : '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Kohëzgjatja</span>
                    <span className="text-emerald-400 font-medium">{formatDuration(s.duration_minutes)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Konsumi</span>
                    <span className="text-yellow-400 font-medium">{formatEnergy(s.energy_kwh)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing Info */}
            <Card className="bg-zinc-800/50 border-white/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-sm font-medium text-white">Faturimi</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Tarifa</span>
                    <span className="text-zinc-300">{s.tariff_lek_per_kwh || 40} LEK/kWh</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Pagesa</span>
                    <span className="text-zinc-300">{s.payment_method === 'wallet' ? 'Wallet' : s.payment_method || 'Wallet'}</span>
                  </div>
                  <div className="flex justify-between border-t border-white/5 pt-2 mt-2">
                    <span className="text-zinc-400 font-medium">Total</span>
                    <span className="text-emerald-400 font-bold text-lg">{formatCurrency(s.cost_lek)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Battery Info */}
            <Card className="bg-zinc-800/50 border-white/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Battery className="w-4 h-4 text-green-400" />
                  <h3 className="text-sm font-medium text-white">Bateria</h3>
                </div>
                {battery?.available ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Fillimi %</span>
                      <span className="text-blue-400">{battery.start_soc_percent ?? '—'}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Mbarimi %</span>
                      <span className="text-emerald-400">{battery.end_soc_percent ?? '—'}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Delta</span>
                      <span className="text-yellow-400 font-medium">
                        {battery.delta_soc_percent != null ? `+${battery.delta_soc_percent}%` : '—'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500 italic">
                    Të dhënat e baterisë nuk janë të disponueshme (N/A nga OCPP)
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Location Info */}
            {site && (
              <Card className="bg-zinc-800/50 border-white/5 md:col-span-2">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-red-400" />
                    <h3 className="text-sm font-medium text-white">Vendndodhja</h3>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{site.name}</p>
                      <p className="text-zinc-400 text-sm">{site.address}, {site.city}</p>
                    </div>
                    {charger && (
                      <div className="text-right">
                        <p className="text-zinc-500 text-xs">Karikues</p>
                        <p className="text-emerald-400 font-mono">{charger.charger_id}</p>
                        <p className="text-zinc-500 text-xs">{charger.vendor} {charger.model}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {invoiceData && (
          <div
            aria-hidden="true"
            style={{ position: 'fixed', left: '-10000px', top: 0, width: '794px', pointerEvents: 'none' }}
          >
            <div ref={invoiceRef}>
              <ChargingInvoiceDocument invoice={invoiceData} />
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <DialogFooter className="border-t border-white/10 pt-4 gap-2">
          <Button variant="outline" onClick={onClose}>
            Mbyll
          </Button>
          {(s.status === 'Charging' || s.status === 'charging') && session?.transaction_id && (
            <Button 
              variant="outline"
              onClick={() => onRemoteStop && onRemoteStop(session)}
              disabled={stoppingSession}
              className="gap-2 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
            >
              <StopCircle className="w-4 h-4" />
              Ndal Karikimin
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={handlePrintInvoice}
            className="gap-2"
          >
            <Printer className="w-4 h-4" />
            Printo
          </Button>
          <Button 
            onClick={handleDownloadInvoice}
            disabled={downloadingPdf}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            {downloadingPdf ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            Shkarko PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionDetailsModal;
